import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccount, useAuth, useSelectedAccount } from '@ibk/shared-auth';
import { clearPendingDomains, clearPermissioningCache, selectHasAccessData, selectLastCheckedDocument, useAppDispatch, useAppSelector, useGetProductRelationShips, useHomeProducts, } from '@ibk/shared-config';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { useGetHomePermissions } from '@ibk/shared-permissioning';
import { useTranslation } from '@ibk/shared-translation';
import { ApiProductStatus, FeatureFlagUseCase, ProductAlias } from '@ibk/shared-types';
import { AccountSelector, Avatar, HeaderBalance, HeaderCompanySelector, HeaderDivider, ProfileMenu, } from '@ibk/shared-ui';
import { getAccountName, localStorageService, StorageKeys } from '@ibk/shared-utils';
import styles from './AppHeaderRight.module.css';
export const AppHeaderRight = ({ onBalanceToggle, onSignOut, className, style, ...props }) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [isProfileMenuVisible, setIsProfileMenuVisible] = useState(false);
    const [isAccountSelectorVisible, setIsAccountSelectorVisible] = useState(false);
    const [menuPosition] = useState({ right: 0 });
    const avatarContainerRef = useRef(null);
    const { finishedValidations } = useGetHomePermissions();
    const { logout } = useAuth();
    const { t } = useTranslation();
    const { selectedAccount, refresh } = useSelectedAccount();
    const { getAccounts, hasMultipleAccounts, updateSelectedAccount } = useAccount();
    const { saveBillingProduct } = useGetProductRelationShips();
    const { isFeatureFlagWhiteListed, getFeatureFlags, clearCache } = useFeatureFlag();
    const userData = useAppSelector(selectHasAccessData);
    const userName = userData?.full_name ?? '';
    const userCpf = useAppSelector(selectLastCheckedDocument) ?? '';
    const { products } = useHomeProducts({
        enabled: !!finishedValidations,
    });
    const contaDigitalData = products[ProductAlias.CONTA_DIGITAL];
    const isContaActive = contaDigitalData?.data?.status === ApiProductStatus.ACTIVE;
    const rawBalance = contaDigitalData?.data?.metadata?.balance?.amount;
    const balance = typeof rawBalance === 'number' ? rawBalance : 0;
    const handleAvatarClick = () => {
        setIsProfileMenuVisible(!isProfileMenuVisible);
    };
    const handleProfileMenuClose = () => {
        setIsProfileMenuVisible(false);
    };
    const handleSignOut = async () => {
        try {
            await logout();
            navigate('/login');
            onSignOut?.();
            dispatch(clearPendingDomains());
            dispatch(clearPermissioningCache());
        }
        catch (error) {
            console.error('Erro ao fazer logout:', error);
            onSignOut?.();
        }
        setIsProfileMenuVisible(false);
    };
    const combinedContainerClassName = [styles.container, className].filter(Boolean).join(' ');
    const profileMenuContainerStyle = {
        ...menuPosition,
    };
    const canShowBalance = useMemo(() => {
        return selectedAccount?.accountId && isContaActive && !contaDigitalData?.isLoading;
    }, [selectedAccount?.accountId, isContaActive, contaDigitalData?.isLoading]);
    const isAccountInfoValid = Boolean(selectedAccount?.branchNumber?.trim() && selectedAccount?.accountNumber?.trim());
    const showAccountSection = isAccountInfoValid && isFeatureFlagWhiteListed(FeatureFlagUseCase.MENU_PERFIL_DADOS_CONTA);
    return (_jsxs("div", { className: combinedContainerClassName, style: style, "data-testid": 'app-header-settings', ...props, children: [_jsx(HeaderCompanySelector, { companyName: getAccountName(selectedAccount), onClick: () => setIsAccountSelectorVisible(true), "data-testid": 'app-header-company-selector', interactive: hasMultipleAccounts() }), showAccountSection && selectedAccount && (_jsxs(_Fragment, { children: [_jsx(HeaderDivider, { "data-testid": 'app-header-divider-1' }), _jsxs("div", { className: styles.accountInfo, "data-testid": 'app-header-account-info', children: [_jsxs("div", { className: styles.accountText, children: [t('account.bank'), _jsx("label", { children: selectedAccount?.branchNumber })] }), _jsxs("div", { className: styles.accountText, children: [t('account.bankAccount'), _jsx("label", { children: selectedAccount?.accountNumber })] })] })] })), _jsx(HeaderDivider, { "data-testid": 'app-header-divider-1' }), _jsx(HeaderBalance, { amount: balance, onVisibilityChange: onBalanceToggle, "data-testid": 'app-header-balance', canShowBalance: !!canShowBalance }), _jsx(HeaderDivider, { "data-testid": 'app-header-divider-2' }), _jsxs("div", { ref: avatarContainerRef, className: styles.avatarContainer, onClick: handleAvatarClick, "data-testid": 'app-header-avatar-container', children: [_jsx(Avatar, { type: 'inicial', initials: userName, size: 'medium', "data-testid": 'app-header-avatar' }), isProfileMenuVisible && (_jsx("div", { className: styles.profileMenuContainer, style: profileMenuContainerStyle, children: _jsx(ProfileMenu, { userName: userName, userCpf: userCpf, isVisible: isProfileMenuVisible, onClose: handleProfileMenuClose, onSignOut: handleSignOut, "data-testid": 'app-header-profile-menu' }) }))] }), isAccountSelectorVisible && (_jsx(AccountSelector, { currentAccount: selectedAccount, availableAccounts: getAccounts(), onClose: () => setIsAccountSelectorVisible(false), onSelect: async (account) => {
                    updateSelectedAccount(account);
                    setIsAccountSelectorVisible(false);
                    await saveBillingProduct(account.targetId);
                    const flagsResult = await getFeatureFlags(true);
                    if (!flagsResult.featureFlags?.length) {
                        clearCache();
                    }
                    window.dispatchEvent(new CustomEvent('account-changed', { detail: { accountId: account.id } }));
                    await localStorageService.removeItem(StorageKeys.PendingPaymentsTotal);
                    await localStorageService.removeItem(StorageKeys.PendingPaymentsList);
                    refresh();
                    window.location.href = `${window.location.origin}/home`;
                } }))] }));
};
export default AppHeaderRight;
